import { Navigate, Outlet, useLocation } from 'react-router-dom'
import { useState, useEffect } from 'react'
import { useAuth } from '@/context/AuthContext'
import VerificationReminderBanner from '@/components/auth/VerificationReminderBanner'
import { getUserByUid } from '@/services/userService'
import { roleRequiresEmailVerification } from '@/services/emailVerificationService'
import { auth } from '@/firebase/config'

const OAUTH_PROVIDERS = ['google.com', 'apple.com', 'facebook.com']

const isOAuthUser = () => {
  const user = auth.currentUser
  if (!user) return false
  return user.providerData.some((provider) =>
    OAUTH_PROVIDERS.includes(provider.providerId)
  )
}

const PrivateRoutes = () => {
  const { currentUser, isLoading, userRole, emailVerified, fetchUserData } =
    useAuth()
  const location = useLocation()
  const [checkingVerification, setCheckingVerification] = useState(false)
  const [isVerifying, setIsVerifying] = useState(null)

  const isVerificationPage =
    location.pathname === '/register/verify-email' ||
    location.pathname === '/verify-email'

  useEffect(() => {
    const checkEmailVerification = async () => {
      if (
        !currentUser ||
        !userRole ||
        isVerificationPage ||
        !roleRequiresEmailVerification(userRole)
      ) {
        return
      }

      if (isOAuthUser() || emailVerified === true) {
        setIsVerifying(true)
        return
      }

      setCheckingVerification(true)

      try {
        const userData = await getUserByUid(currentUser.uid)
        const verified = userData?.emailVerified === true
        setIsVerifying(verified)

        if (verified && emailVerified !== true) {
          await fetchUserData(currentUser.uid)
        }
      } catch (error) {
        console.error('Error checking email verification:', error)
        setIsVerifying(false)
      } finally {
        setCheckingVerification(false)
      }
    }
    checkEmailVerification()
  }, [currentUser, userRole, emailVerified, fetchUserData, isVerificationPage])

  if (isLoading || checkingVerification) {
    return (
      <div
        className='d-flex justify-content-center align-items-center'
        style={{ height: '100vh' }}
      >
        <div className='spinner-border text-primary' role='status'>
          <span className='visually-hidden'>Loading...</span>
        </div>
      </div>
    )
  }

  if (!currentUser) {
    return <Navigate to='/login' replace />
  }

  if (!isVerificationPage && roleRequiresEmailVerification(userRole)) {
    if (!isOAuthUser() && emailVerified !== true) {
      const verifiedStatus = isVerifying !== null ? isVerifying : emailVerified
      if (verifiedStatus === false) {
        return <Navigate to='/register/verify-email' replace />
      }
    }
  }

  return (
    <>
      {!isVerificationPage && <VerificationReminderBanner />}
      <Outlet />
    </>
  )
}

export default PrivateRoutes
